import { TAirtableConsignment } from '@containers/Consignments/ConsignmentHelper';
import { AppMessageCriteriaBase, IAppMessage, IAppMessageCriteria } from '@typings';
import { PLATFORM, stripFormCode } from '@utils/app-message-mappers';
import { ConsignmentStatus } from '@utils/enum-transformers';

/** One row against one screen; the rules are documented on `matchAppMessages`. */
const matches = (m: IAppMessage, criteria: IAppMessageCriteria): boolean =>
    m.active &&
    m.platforms.includes(criteria.platform) &&
    m.routes.includes(criteria.route) &&
    (m.forms.length === 0 || m.forms.some((form) => criteria.forms.includes(form))) &&
    (m.statuses.length === 0 || m.statuses.includes(criteria.status)) &&
    (m.startDate === null || criteria.today >= m.startDate) &&
    (m.endDate === null || criteria.today <= m.endDate);

/**
 * Pure matching rules for `App_Messages` rows (ENVDB-7836). Keep in step with the runbook
 * `docs/ENVDB-7836-airtable-app-messages-runbook.md`:
 *
 * - Blank means NEVER for `platforms` and `routes`, so a half-filled row cannot appear everywhere.
 * - Blank means ANY for `forms`, `statuses`, `startDate` and `endDate`.
 * - `forms` matches when ANY listed form is selected on the consignment.
 * - Dates are `YYYY-MM-DD` strings compared lexically, inclusive at both ends.
 * - `meta` is never read here. It is structured data for future features only.
 *
 * Returns the rows that apply to the given screen, in ascending `order` (stable).
 */
export const matchAppMessages = (messages: IAppMessage[], criteria: IAppMessageCriteria): IAppMessage[] =>
    messages
        .map((m, index) => ({ m, index }))
        .filter(({ m }) => matches(m, criteria))
        .sort((a, b) => a.m.order - b.m.order || a.index - b.index)
        .map(({ m }) => m);

/** The banner body; empty string when the row has none. One column serves both platforms. */
export const resolveMessageText = (m: IAppMessage): string => m.message || '';

/** The subset of a consignment/template the criteria builder reads. */
type ConsignmentLike = {
    status?: string | null;
    forms?: ReadonlyArray<{ type?: string | null } | null> | null;
} | null;

export interface BuildConsignmentCriteriaInput {
    data: TAirtableConsignment | null | undefined;
    /**
     * The forms answer as `useGetStaticAnswer({ field: 'forms' }, data)` returns it: the pending
     * (unsaved) selection as an array of stripped codes when the page is dirty, otherwise the saved
     * `data.forms` objects. Anything that is not an array falls back to `data.forms`.
     *
     * Only the edit wizard passes this. `staticPendingAnswers` is app-global and is cleared on just
     * two exits, so a page that never edits forms (summary, copy, movement) must pass `undefined`
     * or it can inherit another consignment's unsaved selection.
     */
    pendingForms: unknown;
    /** Local calendar date, `YYYY-MM-DD`. */
    today: string;
}

const normaliseForms = (value: unknown): string[] =>
    Array.isArray(value)
        ? value
              .map((form: unknown) =>
                  typeof form === 'string'
                      ? form
                      : form && typeof form === 'object'
                      ? (form as { type?: unknown }).type
                      : undefined
              )
              .filter((code): code is string => typeof code === 'string' && code.trim() !== '')
              .map(stripFormCode)
        : [];

/**
 * Everything in the match criteria except `route`, for a page holding a consignment or template.
 * Pure so the pending-first forms rule is unit-testable without React or the store.
 *
 * `status` comes from the record, defaulting to DRAFT only because an unsaved consignment is a
 * draft — the caller asserts that by handing over a record, rather than the builder guessing.
 */
export const buildConsignmentCriteria = ({
    data,
    pendingForms,
    today,
}: BuildConsignmentCriteriaInput): AppMessageCriteriaBase => {
    const consignment = (data ?? null) as ConsignmentLike;
    const forms = Array.isArray(pendingForms) ? normaliseForms(pendingForms) : normaliseForms(consignment?.forms);

    return {
        platform: PLATFORM,
        forms,
        status: consignment?.status ?? ConsignmentStatus.DRAFT,
        today,
    };
};

/**
 * The criteria for a page that holds no consignment — the list, profile, edecs, admin, register.
 * `forms` and `status` are deliberately empty, so a row carrying either filter can never match
 * here; such rows must leave both blank in Airtable. Documented in the runbook.
 */
export const buildRouteOnlyCriteria = (today: string): AppMessageCriteriaBase => ({
    platform: PLATFORM,
    forms: [],
    status: '',
    today,
});

/**
 * Criteria for the single banner mount in `ProtectedRoute`. Starts from route-only criteria and
 * folds in a page's published forms and status **only when the publication is stamped with this
 * same route** — so facts left by the previous page can never apply to the next one.
 */
export const resolveMountCriteria = (
    route: string,
    publication: { route: string; forms: string[]; status: string } | null,
    today: string
): IAppMessageCriteria => ({
    ...buildRouteOnlyCriteria(today),
    route,
    ...(publication && publication.route === route ? { forms: publication.forms, status: publication.status } : {}),
});
